import styled, { css } from "styled-components";
import { theme } from "./theme";

// Background Wrappers
export const PhaseBackground = styled.div<{
  $variant?: "day" | "night" | "setup";
}>`
  position: relative;
  min-height: 100vh;
  padding-bottom: 4rem;
  background: ${({ $variant = "day" }) => {
    if ($variant === "night") {
      return `radial-gradient(circle at top, rgba(78, 51, 152, 0.3), transparent 55%),
              radial-gradient(circle at bottom right, rgba(18, 12, 36, 0.72), transparent 60%),
              ${theme.colors.background.night}`;
    }
    if ($variant === "setup") {
      return `radial-gradient(circle at top, rgba(124, 93, 255, 0.25), transparent 55%),
              radial-gradient(circle at bottom right, rgba(32, 18, 72, 0.6), transparent 60%),
              ${theme.colors.background.setup}`;
    }
    return `radial-gradient(circle at top, rgba(255, 200, 93, 0.2), transparent 55%),
            radial-gradient(circle at bottom right, rgba(124, 93, 255, 0.32), transparent 60%),
            ${theme.colors.background.day}`;
  }};
  color: ${theme.colors.text.primary};
`;

export const PhaseContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem 3rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1rem 1.25rem 2rem;
  }
`;

export const PhaseMain = styled.div.attrs({ className: "main" })`
  margin-top: 1.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: 1rem !important;
  }
`;

export const PhaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

// Card Components
export const SectionCard = styled.div`
  padding: 1.35rem 1.5rem;
  border-radius: ${theme.borderRadius.xl};
  background: ${theme.colors.card.background};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.md};
`;

export const InstructionCard = styled(SectionCard)`
  background: ${theme.colors.card.instruction};
  border: 1px solid ${theme.colors.card.instructionBorder};
  color: #ffe9a4;
  box-shadow: 0 12px 32px rgba(255, 196, 0, 0.18);

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }
`;

export const SuccessCard = styled(SectionCard)`
  background: ${theme.colors.card.success};
  border: 1px solid ${theme.colors.card.successBorder};
  color: #c8fff0;
  box-shadow: 0 12px 32px rgba(68, 255, 210, 0.18);

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }
`;

export const ErrorCard = styled(SectionCard)`
  background: ${theme.colors.card.error};
  border: 1px solid ${theme.colors.card.errorBorder};
  color: #ffd0d9;
  box-shadow: 0 12px 32px rgba(255, 77, 109, 0.15);

  strong {
    color: #ffffff;
  }
`;

export const MetaCard = styled(SectionCard)`
  background: ${theme.colors.card.meta};
  border: 1px solid ${theme.colors.card.metaBorder};
  color: ${theme.colors.text.secondary};
`;

// Table Components
export const PlayersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.card.background};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};

  thead th {
    padding: 1rem 1.25rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(245, 242, 255, 0.82);
  }

  tbody tr {
    transition: background ${theme.transitions.normal};
  }

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }

  tbody tr:hover {
    background: rgba(124, 93, 255, 0.12);
  }

  tbody td {
    padding: 0.9rem 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
  }
`;

export const NumberCell = styled.td`
  width: 56px;
  font-weight: 700;
  font-size: 0.95rem;
`;

export const FoulCell = styled.td`
  width: 80px;
`;

// Player Components
export const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const PlayerAvatar = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.12);
`;

export const PlayerName = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

// Button Variants
export const buttonVariants = {
  primary: css`
    background: ${theme.colors.button.primary};
    border: 1px solid ${theme.colors.border.strong};

    &:hover:not(:disabled) {
      background: ${theme.colors.button.primaryHover};
    }
  `,
  secondary: css`
    background: ${theme.colors.button.secondary};
    border: 1px solid ${theme.colors.border.secondary};

    &:hover:not(:disabled) {
      background: ${theme.colors.button.secondaryHover};
    }
  `,
  danger: css`
    background: ${theme.colors.button.danger};
    border: 1px solid rgba(255, 96, 122, 0.5);

    &:hover:not(:disabled) {
      background: ${theme.colors.button.dangerHover};
    }
  `,
  outline: css`
    background: transparent;
    border: 1px solid ${theme.colors.border.secondary};
    color: ${theme.colors.text.primary};

    &:hover:not(:disabled) {
      background: ${theme.colors.button.secondary};
    }
  `,
  ghost: css`
    background: transparent;
    border: 1px solid transparent;
    color: ${theme.colors.text.muted};

    &:hover:not(:disabled) {
      color: ${theme.colors.text.primary};
      background: ${theme.colors.button.secondary};
    }
  `,
} as const;

export type ButtonVariant = keyof typeof buttonVariants;

export const PhaseButton = styled.button.attrs({ type: "button" })<{
  $variant?: ButtonVariant;
}>`
  border-radius: ${theme.borderRadius.md};
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: transform 0.15s ease-out, background 0.15s ease-out, color 0.15s ease-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  ${({ $variant = "primary" }) => buttonVariants[$variant]};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// Badge Components
export const FoulBadge = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid ${theme.colors.border.secondary};
  color: ${theme.colors.text.primary};
  font-weight: 700;
  transition: background 0.15s ease-out, transform 0.15s ease-out;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 3, 12, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1050;
`;

export const ModalCard = styled.div`
  width: min(520px, 100%);
  max-height: 90vh;
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border-radius: ${theme.borderRadius.xxl};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.xl};
  color: ${theme.colors.text.primary};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem 0;
`;

export const ModalTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #dcd4ff;
`;

export const ModalCloseButton = styled.button.attrs({ type: "button" })`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${theme.colors.border.secondary};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease-out, transform 0.15s ease-out;
  font-size: 1.5rem;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
  }
`;

export const ModalBody = styled.div`
  padding: 1.25rem 1.75rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

export const ModalFooter = styled.div`
  padding: 0 1.75rem 1.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
`;

// Utility Components
export const ActionBar = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

export const MetaInfo = styled.div`
  text-align: center;
  font-size: 0.85rem;
  line-height: 1.6;
  color: inherit;
`;

export const MetaHighlight = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 600;
`;

export const MutedText = styled.span`
  opacity: 0.4;
  font-size: 0.85rem;
`;

// Player Management Components (for LastWords and MorningTransition)
export const PlayerManagementSection = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const ManagementToggle = styled.button.attrs({ type: 'button' })`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

export const ToggleIcon = styled.span<{ $expanded: boolean }>`
  font-size: 1rem;
  transition: transform ${theme.transitions.normal};
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

export const ManagementContent = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height ${theme.transitions.normal} ease-in-out;
`;

export const ManagementInner = styled.div`
  padding-top: ${theme.spacing.md};
`;

export const FoulDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

export const FoulCountBadge = styled.span<{ $count: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  font-size: 1rem;
  background: ${({ $count }) => {
    if ($count >= 4) return 'linear-gradient(135deg, rgba(255, 77, 109, 0.35), rgba(200, 50, 80, 0.35))';
    if ($count === 3) return 'linear-gradient(135deg, rgba(255, 196, 0, 0.35), rgba(200, 150, 0, 0.35))';
    if ($count >= 1) return 'linear-gradient(135deg, rgba(255, 152, 0, 0.25), rgba(200, 100, 0, 0.25))';
    return 'rgba(255, 255, 255, 0.08)';
  }};
  border: 1px solid ${({ $count }) => {
    if ($count >= 4) return 'rgba(255, 77, 109, 0.5)';
    if ($count === 3) return 'rgba(255, 196, 0, 0.5)';
    if ($count >= 1) return 'rgba(255, 152, 0, 0.4)';
    return 'rgba(255, 255, 255, 0.16)';
  }};
  color: ${theme.colors.text.primary};
`;

export const FoulLabel = styled.span`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

export const ManagementButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

export const ManagementButton = styled(PhaseButton)`
  flex: 1;
  min-width: 140px;
  font-size: 0.9rem;
  padding: 0.75rem ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

export const PPKButton = styled(ManagementButton)`
  background: linear-gradient(135deg, rgba(138, 43, 226, 0.25), rgba(75, 0, 130, 0.35));
  border-color: rgba(138, 43, 226, 0.45);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(138, 43, 226, 0.35), rgba(75, 0, 130, 0.45));
  }
`;

export const DisqualifyButton = styled(ManagementButton)`
  background: linear-gradient(135deg, rgba(255, 77, 109, 0.25), rgba(200, 50, 80, 0.35));
  border-color: rgba(255, 77, 109, 0.45);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(255, 77, 109, 0.35), rgba(200, 50, 80, 0.45));
  }
`;
